import {
  OrderHistoryReceipt,
  OrderHistoryReceiptMeal,
  OvenCookHistoryItem,
} from '@tovala/browser-apis-combinedapi'
import { orderBy } from 'lodash-es'

import { getSourceIDFromPathname, sourceIDs } from 'analytics/events'
import {
  CreateMealReviewRequest,
  RatingChip,
} from 'components/core/service/combinedapi/reviews'
import { ErrorCodeMessageMapCombinedAPI } from 'types/internal'
import { DATE_FORMATS, formatDate, isDateSame } from 'utils/dates'
import { minutesToMs } from 'utils/general'
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from 'utils/storage'
import { getMostRecentCookByMealID, isRateableReceipt } from 'utils/terms'

const MIN_MINUTES_SINCE_COOK = 20
const MAX_DAYS_SINCE_COOK = 7
const MIN_MINUTES_BETWEEN_PROMPTS = 20
const DISMISSALS_BEFORE_PAUSE = 3
const PAUSE_DAYS_AFTER_DISMISSALS = 30
const MAX_SEEN_SINGLE_MEAL_IDS = 100

export const RATING_PER_STAR = 20
export const NEUTRAL_RATING = 60

export const REVIEW_CHARACTER_LIMIT = 1000
export const REVIEW_CHARACTER_LIMIT_ERROR =
  "You've hit the 1,000 character limit. Please shorten your review to post."

export const REVIEW_COMMENT_RULES = {
  maxLength: {
    message: REVIEW_CHARACTER_LIMIT_ERROR,
    value: REVIEW_CHARACTER_LIMIT,
  },
}

export const CREATE_REVIEW_ERRORS: ErrorCodeMessageMapCombinedAPI = {
  Fallback: {
    helpToFix: 'Please try again.',
    why: "We couldn't save your feedback due to a technical issue on our end.",
  },
}

export const LOAD_RATING_CHIPS_ERRORS: ErrorCodeMessageMapCombinedAPI = {
  Fallback: {
    helpToFix: 'Please reload the page and try again.',
    why: "We couldn't load feedback suggestions due to a technical issue on our end.",
  },
}

export interface MealToRate {
  cookedAt: Date | null
  meal: OrderHistoryReceiptMeal
}

export type RatingPromptToShow =
  | { type: 'multi'; mealsToRate: MealToRate[] }
  | { type: 'single'; mealToRate: MealToRate }

export interface RatingPromptHistory {
  consecutiveDismissals: number
  lastShownAt: string | null
  pausedUntil: string | null
  seenSingleMealIDs: number[]
}

export function getRatingChipsForRating({
  chips,
  rating,
}: {
  chips: RatingChip[]
  rating: number
}) {
  return orderBy(
    chips.filter(
      (chip) => chip.minRating <= rating && rating <= chip.maxRating,
    ),
    'displayOrder',
  )
}

export function getCreateMealReviewData({
  chips,
  comment,
  mealID,
  rating,
  selectedChipIDs,
  sharePublicly,
  userID,
}: {
  chips: RatingChip[]
  comment: string
  mealID: number
  rating: number
  selectedChipIDs: string[]
  sharePublicly: boolean
  userID: number
}): CreateMealReviewRequest['data'] {
  const chipIDsForRating = new Set(
    getRatingChipsForRating({ chips, rating }).map((chip) => chip.id),
  )

  return {
    comment,
    mealid: mealID,
    platform: 'web',
    rating,
    ratingType: 'stars',
    routineRevisionID: null,
    selectedChipIDs: selectedChipIDs.filter((chipID) =>
      chipIDsForRating.has(chipID),
    ),
    sharePublicly,
    userid: userID,
  }
}

export function getReviewSourceID({
  pathname,
  sourceID,
}: {
  pathname: string
  sourceID?: string
}) {
  if (sourceID) {
    return sourceID
  }

  const sourceIDFromPathname = getSourceIDFromPathname(pathname)

  return sourceIDFromPathname === sourceIDs.SETTINGS
    ? sourceIDs.ORDER_HISTORY
    : sourceIDFromPathname
}

export function joinMealIDs(mealIDs: number[]) {
  return mealIDs.join(',')
}

export function getMealsToRate({
  cookHistory,
  now,
  orderHistoryReceipts,
  reviewedMealIDs,
}: {
  cookHistory: OvenCookHistoryItem[]
  now: Date
  orderHistoryReceipts: OrderHistoryReceipt[]
  reviewedMealIDs: number[]
}): MealToRate[] {
  const reviewedMealIDsSet = new Set(reviewedMealIDs)
  const receiptMealsByID = getReceiptMealsByID(
    orderHistoryReceipts.filter(isRateableReceipt),
  )
  const mostRecentCookByMealID = getMostRecentCookByMealID(
    cookHistory.filter((cook) => isCookInPromptWindow({ cook, now })),
  )

  return orderBy(
    Array.from(mostRecentCookByMealID.entries()),
    ([, cook]) => new Date(cook.end_time).getTime(),
    'desc',
  ).flatMap(([mealID, cook]) => {
    const meal = receiptMealsByID.get(mealID)

    return meal && !reviewedMealIDsSet.has(mealID)
      ? [{ cookedAt: new Date(cook.end_time), meal }]
      : []
  })
}

export function getRatingPromptToShow({
  hasViewedOnboarding,
  mealsToRate,
  seenSingleMealIDs,
}: {
  hasViewedOnboarding: boolean
  mealsToRate: MealToRate[]
  seenSingleMealIDs: number[]
}): RatingPromptToShow | null {
  if (hasViewedOnboarding && mealsToRate.length > 1) {
    return { type: 'multi', mealsToRate }
  }

  const seenSingleMealIDsSet = new Set(seenSingleMealIDs)
  const mealToRate = mealsToRate.find(
    ({ meal }) => !seenSingleMealIDsSet.has(meal.id),
  )

  return mealToRate ? { type: 'single', mealToRate } : null
}

export function getCookedDayLabel({
  cookedAt,
  now,
}: {
  cookedAt: Date
  now: Date
}) {
  if (isDateSame(cookedAt, now, { units: 'days' })) {
    return 'Cooked today'
  }

  return `Cooked ${formatDate(cookedAt, { format: DATE_FORMATS.DOW_FULL })}`
}

export function isRatingPromptSuppressed({
  history: { lastShownAt, pausedUntil },
  now,
}: {
  history: RatingPromptHistory
  now: Date
}) {
  const isPaused =
    pausedUntil !== null && now.getTime() < new Date(pausedUntil).getTime()
  const isThrottled =
    lastShownAt !== null &&
    now.getTime() - new Date(lastShownAt).getTime() <
      minutesToMs(MIN_MINUTES_BETWEEN_PROMPTS)

  return isPaused || isThrottled
}

export function getRatingPromptHistory(userID: number): RatingPromptHistory {
  const storedHistory = getLocalStorageItem<Partial<RatingPromptHistory>>(
    getRatingPromptStorageKey(userID),
  )

  return {
    consecutiveDismissals:
      typeof storedHistory?.consecutiveDismissals === 'number'
        ? storedHistory.consecutiveDismissals
        : 0,
    lastShownAt:
      typeof storedHistory?.lastShownAt === 'string'
        ? storedHistory.lastShownAt
        : null,
    pausedUntil:
      typeof storedHistory?.pausedUntil === 'string'
        ? storedHistory.pausedUntil
        : null,
    seenSingleMealIDs: Array.isArray(storedHistory?.seenSingleMealIDs)
      ? storedHistory.seenSingleMealIDs
      : [],
  }
}

export function recordRatingPromptShown({
  now,
  singleMealID,
  userID,
}: {
  now: Date
  singleMealID?: number
  userID: number
}) {
  const history = getRatingPromptHistory(userID)

  saveRatingPromptHistory(userID, {
    ...history,
    lastShownAt: now.toISOString(),
    seenSingleMealIDs:
      singleMealID === undefined
        ? history.seenSingleMealIDs
        : [
            singleMealID,
            ...history.seenSingleMealIDs.filter(
              (seenMealID) => seenMealID !== singleMealID,
            ),
          ].slice(0, MAX_SEEN_SINGLE_MEAL_IDS),
  })
}

export function recordRatingPromptDismissed({
  now,
  userID,
}: {
  now: Date
  userID: number
}) {
  const history = getRatingPromptHistory(userID)
  const consecutiveDismissals = history.consecutiveDismissals + 1

  if (consecutiveDismissals < DISMISSALS_BEFORE_PAUSE) {
    saveRatingPromptHistory(userID, { ...history, consecutiveDismissals })
    return
  }

  saveRatingPromptHistory(userID, {
    ...history,
    consecutiveDismissals: 0,
    pausedUntil: new Date(
      now.getTime() + minutesToMs(PAUSE_DAYS_AFTER_DISMISSALS * 24 * 60),
    ).toISOString(),
  })
}

export function recordRatingPromptRated(userID: number) {
  saveRatingPromptHistory(userID, {
    ...getRatingPromptHistory(userID),
    consecutiveDismissals: 0,
  })
}

function saveRatingPromptHistory(userID: number, history: RatingPromptHistory) {
  setLocalStorageItem(getRatingPromptStorageKey(userID), history)
}

function getRatingPromptStorageKey(userID: number) {
  return `meal_rating_prompt_${userID}`
}

export function getLastSharePubliclyChoice(userID: number) {
  return (
    getLocalStorageItem<boolean>(getSharePubliclyStorageKey(userID)) !== false
  )
}

export function saveSharePubliclyChoice({
  sharePublicly,
  userID,
}: {
  sharePublicly: boolean
  userID: number
}) {
  setLocalStorageItem(getSharePubliclyStorageKey(userID), sharePublicly)
}

function getSharePubliclyStorageKey(userID: number) {
  return `meal_rating_share_publicly_${userID}`
}

function isCookInPromptWindow({
  cook,
  now,
}: {
  cook: OvenCookHistoryItem
  now: Date
}) {
  if (!cook.meal_id) {
    return false
  }

  const msSinceCook = now.getTime() - new Date(cook.end_time).getTime()

  return (
    msSinceCook >= minutesToMs(MIN_MINUTES_SINCE_COOK) &&
    msSinceCook <= minutesToMs(MAX_DAYS_SINCE_COOK * 24 * 60)
  )
}

export function getReceiptMealsByID(
  orderHistoryReceipts: OrderHistoryReceipt[],
) {
  const receiptMealsByID = new Map<number, OrderHistoryReceiptMeal>()

  orderHistoryReceipts.forEach((receipt) => {
    receipt.selectedMeals?.forEach((meal) => {
      if (!receiptMealsByID.has(meal.id)) {
        receiptMealsByID.set(meal.id, meal)
      }
    })
  })

  return receiptMealsByID
}

export function hasViewedRatingOnboarding(userID: number) {
  return (
    getLocalStorageItem<boolean>(
      getRatingOnboardingViewedStorageKey(userID),
    ) === true
  )
}

export function markRatingOnboardingViewed(userID: number) {
  setLocalStorageItem(getRatingOnboardingViewedStorageKey(userID), true)
}

export function resetRatingOnboardingViewed(userID: number) {
  removeLocalStorageItem(getRatingOnboardingViewedStorageKey(userID))
}

function getRatingOnboardingViewedStorageKey(userID: number) {
  return `meal_rating_onboarding_viewed_${userID}`
}
